import { useEffect, useState } from "react";
import { API_URL } from "../../config";
import Flashcard from "./Flashcard";
import Quiz from "./Quiz";

interface Module {
  id: string;
  title: string;
  description: string;
  learning_objectives: string[];
  content: string;
  practice_guidance: string;
  order: number;
  prerequisites: string[];
  youtube_query: string;
}

interface FlashcardData {
  id: string;
  front: string;
  back: string;
}

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correct_index: number;
  explanation: string;
}

interface YouTubeVideo {
  videoId: string;
  title: string;
  thumbnail: string;
  channelTitle: string;
}

interface ModuleDetailData {
  content: string;
  learning_objectives: string[];
  practice_guidance: string;
  flashcards: FlashcardData[];
  quiz: QuizQuestion[];
}

interface ModuleDetailProps {
  module: Module;
  courseTopic: string;
  isCompleted: boolean;
  isLocked: boolean;
  numFlashcards: number;
  numQuiz: number;
  onMarkComplete: (moduleId: string) => void;
  onClose: () => void;
}

type Tab = "content" | "practice" | "videos";

const detailCache: Record<string, ModuleDetailData> = {};

function renderMarkdown(text: string) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements: JSX.Element[] = [];
  let inCodeBlock = false;
  let codeLines: string[] = [];

  lines.forEach((line, i) => {
    if (line.trim().startsWith("```")) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${i}`}>
            <code>{codeLines.join("\n")}</code>
          </pre>
        );
        codeLines = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      return;
    }
    if (inCodeBlock) {
      codeLines.push(line);
      return;
    }
    if (line.startsWith("#### ")) {
      elements.push(<h5 key={i}>{renderInline(line.slice(5))}</h5>);
    } else if (line.startsWith("### ")) {
      elements.push(<h4 key={i}>{renderInline(line.slice(4))}</h4>);
    } else if (line.startsWith("## ")) {
      elements.push(<h3 key={i}>{renderInline(line.slice(3))}</h3>);
    } else if (line.startsWith("# ")) {
      elements.push(<h2 key={i}>{renderInline(line.slice(2))}</h2>);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      elements.push(
        <div key={i} className="md-list-item">
          <span className="md-bullet" />
          {renderInline(line.slice(2))}
        </div>
      );
    } else if (/^\s*\d+\.\s/.test(line)) {
      const match = line.match(/^(\s*\d+)\.\s(.*)$/);
      if (match) {
        elements.push(
          <div key={i} className="md-list-item numbered">
            <span className="md-number">{match[1]}.</span>
            {renderInline(match[2])}
          </div>
        );
      } else {
        elements.push(<p key={i}>{renderInline(line)}</p>);
      }
    } else if (line.trim().startsWith("> ")) {
      elements.push(
        <blockquote key={i} className="md-blockquote">
          {renderInline(line.trim().slice(2))}
        </blockquote>
      );
    } else if (line.trim() === "") {
      elements.push(<br key={i} />);
    } else {
      elements.push(<p key={i}>{renderInline(line)}</p>);
    }
  });

  if (inCodeBlock && codeLines.length > 0) {
    elements.push(
      <pre key="code-end">
        <code>{codeLines.join("\n")}</code>
      </pre>
    );
  }
  return <>{elements}</>;
}

function renderInline(text: string) {
  const parts = text.split(/(`[^`]+`)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("`") && part.endsWith("`") ? (
          <code key={i}>{part.slice(1, -1)}</code>
        ) : (
          <span key={i}>{renderBold(part)}</span>
        )
      )}
    </>
  );
}

function renderBold(text: string) {
  const parts = text.split(/(\*\*.*?\*\*|\*[^*]+\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i}>{part.slice(2, -2)}</strong>
        ) : part.startsWith("*") && part.endsWith("*") && part.length > 2 ? (
          <em key={i}>{part.slice(1, -1)}</em>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export default function ModuleDetail({
  module,
  courseTopic,
  isCompleted,
  isLocked,
  numFlashcards,
  numQuiz,
  onMarkComplete,
  onClose,
}: ModuleDetailProps) {
  const [activeTab, setActiveTab] = useState<Tab>("content");
  const [detail, setDetail] = useState<ModuleDetailData | null>(
    detailCache[module.id] || null
  );
  const [loading, setLoading] = useState(!detailCache[module.id]);
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);
  const [retesting, setRetesting] = useState(false);
  const [askQuestion, setAskQuestion] = useState("");
  const [askLoading, setAskLoading] = useState(false);
  const [qaHistory, setQaHistory] = useState<{ question: string; answer: string }[]>([]);

  useEffect(() => {
    if (detailCache[module.id]) {
      setDetail(detailCache[module.id]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/course/module/detail`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        topic: courseTopic,
        module_title: module.title,
        module_description: module.description,
        num_flashcards: numFlashcards,
        num_quiz: numQuiz,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === "success" && data.detail) {
          detailCache[module.id] = data.detail;
          setDetail(data.detail);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [module.id, module.title, module.description, courseTopic, numFlashcards, numQuiz]);

  useEffect(() => {
    if (!module.youtube_query) return;
    setLoadingVideos(true);
    setVideos([]);
    fetch(
      `${API_URL}/api/course/youtube?q=${encodeURIComponent(module.youtube_query)}&maxResults=10`
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.status === "success") setVideos(data.videos || []);
      })
      .catch(() => {})
      .finally(() => setLoadingVideos(false));
  }, [module.id, module.youtube_query]);

  const handleAsk = async () => {
    const q = askQuestion.trim();
    if (!q || askLoading) return;
    setAskQuestion("");
    setAskLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/course/module/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic: courseTopic, module_title: module.title, question: q }),
      });
      const data = await r.json();
      if (data.status === "success") {
        setQaHistory((prev) => [...prev, { question: q, answer: data.answer }]);
      }
    } catch {}
    finally { setAskLoading(false); }
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: "content", label: "Content" },
    { key: "practice", label: "Practice" },
    { key: "videos", label: "Videos" },
  ];

  return (
    <div className="module-detail">
      <div className="module-detail-header">
        <h2>{module.title}</h2>
        <button className="close-btn" onClick={onClose}>
          x
        </button>
      </div>

      <div className="module-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`module-tab ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="module-detail-content">
        {loading ? (
          <div className="module-skeleton">
            <div className="skeleton-line w80" />
            <div className="skeleton-line w60" />
            <div className="skeleton-line w90" />
            <div className="skeleton-line w70" />
            <div className="skeleton-line w85" />
            <div className="skeleton-line w50" />
            <p style={{ color: "#555", marginTop: "1rem", fontSize: "0.8rem" }}>
              Generating content with AI...
            </p>
          </div>
        ) : (
          <>
            {activeTab === "content" && detail && (
              <>
                <div style={{ marginBottom: "1rem" }}>
                  <span
                    className={`module-status-badge ${isCompleted ? "completed" : isLocked ? "locked" : "available"}`}
                  >
                    {isCompleted ? "Completed" : isLocked ? "Locked" : "Available"}
                  </span>
                  <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", color: "#707577" }}>
                    Module {module.order}
                  </span>
                </div>

                {detail.learning_objectives?.length > 0 && (
                  <div className="module-section">
                    <h3>Learning Objectives</h3>
                    <ul>
                      {detail.learning_objectives.map((obj, i) => (
                        <li key={i}>{obj}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="module-section">
                  <h3>Content</h3>
                  <div className="module-content-text">
                    {renderMarkdown(detail.content)}
                  </div>
                </div>

                {detail.practice_guidance && (
                  <div className="module-section">
                    <h3>Practice</h3>
                    <div className="practice-box">{renderMarkdown(detail.practice_guidance)}</div>
                  </div>
                )}

                {qaHistory.map((qa, i) => (
                  <div key={i} className="qa-entry">
                    <div className="qa-question">
                      <span className="qa-label">You</span>
                      {qa.question}
                    </div>
                    <div className="qa-answer">
                      <span className="qa-label ai">AI</span>
                      <div className="module-content-text">{renderMarkdown(qa.answer)}</div>
                    </div>
                  </div>
                ))}

                <div className="ask-ai-bar">
                  <input
                    type="text"
                    className="ask-ai-input"
                    placeholder="Want to know more? Ask AI..."
                    value={askQuestion}
                    onChange={(e) => setAskQuestion(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAsk()}
                    disabled={askLoading}
                  />
                  <button
                    className="ask-ai-btn"
                    onClick={handleAsk}
                    disabled={askLoading || !askQuestion.trim()}
                  >
                    {askLoading ? "..." : "Ask"}
                  </button>
                </div>

              </>
            )}

            {activeTab === "practice" && detail && (
              <div className="practice-tab">
                <div className="practice-section">
                  <h3 className="practice-section-title">Flashcards</h3>
                  <Flashcard flashcards={detail.flashcards || []} />
                </div>

                <div className="practice-divider" />

                <div className="practice-section">
                  <h3 className="practice-section-title">Quiz</h3>
                  {retesting ? (
                    <div className="quiz-retesting">
                      <div className="mini-spinner" />
                      <span>Generating new questions...</span>
                    </div>
                  ) : (
                    <Quiz
                      questions={detail.quiz || []}
                      onQuizFinish={(passed) => {
                        if (passed) setQuizPassed(true);
                      }}
                      onRetest={() => {
                        setRetesting(true);
                        setQuizPassed(false);
                        fetch(`${API_URL}/api/course/module/detail`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            topic: courseTopic,
                            module_title: module.title,
                            module_description: module.description,
                            num_flashcards: numFlashcards,
                            num_quiz: numQuiz,
                          }),
                        })
                          .then((r) => r.json())
                          .then((data) => {
                            if (data.status === "success" && data.detail) {
                              const updated = { ...detailCache[module.id], quiz: data.detail.quiz };
                              detailCache[module.id] = updated;
                              setDetail(updated);
                            }
                          })
                          .catch(() => {})
                          .finally(() => setRetesting(false));
                      }}
                    />
                  )}
                </div>

                {!isLocked && (quizPassed || isCompleted) && (
                  <button
                    className={`mark-complete-btn ${isCompleted ? "completed" : ""}`}
                    onClick={() => !isCompleted && onMarkComplete(module.id)}
                    disabled={isCompleted}
                  >
                    {isCompleted ? "Completed" : "Mark as Complete"}
                  </button>
                )}
              </div>
            )}

            {activeTab === "videos" && (
              <>
                {loadingVideos ? (
                  <div className="youtube-loading">
                    <div className="mini-spinner" />
                    <span>Loading videos...</span>
                  </div>
                ) : videos.length > 0 ? (
                  <div className="youtube-grid">
                    {videos.map((v) => (
                      <a
                        key={v.videoId}
                        className="youtube-card"
                        href={`https://www.youtube.com/watch?v=${v.videoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img src={v.thumbnail} alt={v.title} />
                        <div className="video-info">
                          <div className="video-title">{v.title}</div>
                          <div className="video-channel">{v.channelTitle}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: "0.8rem", color: "#707577" }}>
                    No videos found
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
